import ELK from 'elkjs/lib/elk.bundled.js';
import { Edge, MarkerType, Node, Position } from 'reactflow';
import type { WorkflowStepWidth } from 'src/gql/graphql';
import {
  type WorkflowEdgeData as DefinitionEdgeData,
  getStepOutputs,
  isValidEdgeShape,
  isValidNodeShape,
  isWorkflowDefinitionShape,
  isWorkflowNodeType,
  ParsedEdgeShape,
  ParsedNodeShape,
  ParsedWorkflowDefinition,
  WorkflowDefinition,
  WorkflowDefinitionEdge,
  WorkflowDefinitionNode,
  WorkflowInsertionPoint,
  WorkflowNodeType,
  type WorkflowNodeTypeValue,
  WorkflowStepCatalog,
} from './workflow-definition';
import { getOpenOutputs } from './workflow-definition-edits';
import { withoutFoldedSteps } from './workflow-folding';

/**
 * What a step card needs to draw itself before it reads the step: its title and colours. The
 * step's data, summary and problems come from `WorkflowEditorContext` by `nodeId`, so typing in a
 * field re-renders the card without rebuilding the graph or re-running the layout.
 */
export interface WorkflowNodeDisplayData {
  nodeType: string;
  nodeId: string;
  /** The catalog doesn't describe `nodeType`: drawn as "Unknown step", read-only, saved untouched. */
  isUnknown: boolean;
  /**
   * The width the layout made room for. The card itself draws at its width from
   * `WorkflowEditorContext`, which is ahead of this while a resize is being laid out.
   */
  layoutWidth: number;
  /** Laid out left to right: connections enter on the left and leave on the right. */
  horizontal?: boolean;
  icon?: string;
  iconColor?: string;
  headerColor?: string;
  accentColor?: string;
  detailsLabel?: string;
  /**
   * Where a branching card's outputs leave it, as a share of its leaving edge; absent for a step
   * with a single `next`. The layout pins its ports here and the card draws its handles here.
   */
  sourceHandles?: SourceHandleOffset[];
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export interface SourceHandleOffset {
  id: string;
  offset: number;
}

/** The + at the end of a path, where a step can be added. An empty `if` output uses its chip. */
export interface WorkflowAddStepNodeData {
  insertion: Extract<WorkflowInsertionPoint, { kind: 'output' }>;
  /** Laid out left to right: the + sits at the left, where the connection arrives. */
  horizontal?: boolean;
}

/** A connection between two steps; `editable` puts the + on it. */
export interface WorkflowEdgeData {
  definitionEdgeId: string;
  editable: boolean;
}

export type WorkflowGraphNode = Node<WorkflowNodeDisplayData | WorkflowAddStepNodeData>;
/** Step connections carry `WorkflowEdgeData`; the lines to add-step targets carry none. */
export type WorkflowGraphEdge = Edge<WorkflowEdgeData | undefined>;

interface ReactFlowGraph {
  nodes: WorkflowGraphNode[];
  edges: WorkflowGraphEdge[];
}

export interface BuildWorkflowGraphOptions {
  catalog: WorkflowStepCatalog;
  isDarkMode?: boolean;
  layoutDirection?: WorkflowLayoutDirection;
  /** A draft the user can edit: connections get a +, and open outputs get an add-step target. */
  editable?: boolean;
  /** Folded `if` outputs (see `outputKey`); the steps only they lead to are left out. */
  folded?: ReadonlySet<string>;
}

const COLUMN_COUNT = 3;
const COLUMN_WIDTH = 320;
const ROW_HEIGHT = 180;
const ELK_NODE_WIDTH = 280;
const ELK_NODE_HEIGHT = 160;
const ELK_ADD_STEP_HEIGHT = 56;

/** A step type's card width limits, from the catalog. */
export type WorkflowStepWidthLimits = Omit<WorkflowStepWidth, '__typename'>;

/** The width of a step whose type the catalog doesn't know, and every step's width before 1V-6204. */
export const DEFAULT_STEP_WIDTH = 280;

const STEP_WIDTH_SNAP = 10;

const UNKNOWN_STEP_WIDTH_LIMITS: WorkflowStepWidthLimits = {
  default: DEFAULT_STEP_WIDTH,
  min: DEFAULT_STEP_WIDTH,
  max: DEFAULT_STEP_WIDTH,
};

/** The catalog's limits for `type`; `DEFAULT_STEP_WIDTH` fixed at both ends for an unknown type. */
export function getStepWidthLimits(
  type: string,
  catalog: WorkflowStepCatalog,
): WorkflowStepWidthLimits {
  const width = catalog.byType.get(type)?.width;
  return width
    ? { default: width.default, min: width.min, max: width.max }
    : UNKNOWN_STEP_WIDTH_LIMITS;
}

/** `width` snapped to 10px and held inside `limits`. */
export function clampStepWidth(width: number, limits: WorkflowStepWidthLimits): number {
  const snapped = Math.round(width / STEP_WIDTH_SNAP) * STEP_WIDTH_SNAP;
  return Math.min(limits.max, Math.max(limits.min, snapped));
}

/** The width a step's card draws at: its own width clamped to its type's limits, else the default. */
export function getStepWidth(
  node: Pick<WorkflowDefinitionNode, 'type' | 'width'>,
  catalog: WorkflowStepCatalog,
): number {
  const limits = getStepWidthLimits(node.type, catalog);
  return node.width === undefined ? limits.default : clampStepWidth(node.width, limits);
}

export const STEP_NODE_TYPE = 'workflowNode';
export const ADD_STEP_NODE_TYPE = 'workflowAddStep';
export const STEP_EDGE_TYPE = 'workflowEdge';

const isStepNode = (node: WorkflowGraphNode): node is Node<WorkflowNodeDisplayData> =>
  node.type === STEP_NODE_TYPE;

/**
 * A fresh layout of `next`, moved so that a step whose width alone changed since `previous` stays
 * where it was on screen. ELK centres a step in its row, so a widened step grows to both sides;
 * holding it still keeps its right edge, and the grip, under the pointer while its neighbours
 * move apart. Any other change — a step added, removed or folded away, two widths at once — lays
 * out as is.
 */
export function keepResizedStepInPlace(
  next: WorkflowGraphNode[],
  previous: WorkflowGraphNode[],
): WorkflowGraphNode[] {
  if (next.length !== previous.length) return next;

  const before = new Map(previous.filter(isStepNode).map((node) => [node.id, node]));
  const steps = next.filter(isStepNode);
  if (steps.length !== before.size || steps.some(({ id }) => !before.has(id))) return next;

  const resized = steps.filter(
    ({ id, data }) => before.get(id)?.data.layoutWidth !== data.layoutWidth,
  );
  if (resized.length !== 1) return next;

  const [step] = resized;
  const was = before.get(step.id)!.position;
  const dx = was.x - step.position.x;
  const dy = was.y - step.position.y;
  if (!dx && !dy) return next;

  return next.map((node) => ({
    ...node,
    position: { x: node.position.x + dx, y: node.position.y + dy },
  }));
}

export enum WorkflowLayoutDirection {
  Down = 'DOWN',
  Right = 'RIGHT',
}

/**
 * Where an `if` card's outputs leave it, as a share of its leaving edge: true first (left down the
 * page, top across it), false second. The layout pins its ports here, so a branch never swaps
 * sides however long it grows.
 */
export const IF_HANDLE_OFFSETS = { 'if-true': 0.25, 'if-false': 0.75 } as const;

/**
 * The handle an output leaves its card from: `if-true` / `if-false` on an `if`, `option:<key>` on a
 * Phone menu, none for the single `next` of other steps.
 */
export const outputHandleId = (output: DefinitionEdgeData | undefined): string | undefined => {
  if (typeof output?.condition === 'boolean') return output.condition ? 'if-true' : 'if-false';
  if (output?.option) return `option:${output.option}`;
  return undefined;
};

/**
 * Where a branching step's outputs leave its card. An `if` keeps its fixed true / false ports; a
 * Phone menu spreads one per option, then no selection, evenly across the edge, in the order the
 * options are listed.
 */
export const sourceHandleOffsets = (
  node: WorkflowDefinitionNode,
  catalog: WorkflowStepCatalog,
): SourceHandleOffset[] | undefined => {
  if (node.type === WorkflowNodeType.If) {
    return Object.entries(IF_HANDLE_OFFSETS).map(([id, offset]) => ({ id, offset }));
  }

  const stepType = catalog.byType.get(node.type);
  if (node.type !== WorkflowNodeType.GatherMenu || !stepType) return undefined;

  const outputs = getStepOutputs(node, stepType);
  return outputs.flatMap(({ option }, index) =>
    option ? [{ id: `option:${option}`, offset: (index + 0.5) / outputs.length }] : [],
  );
};

const elk = new ELK();
const getElkOptions = (direction: WorkflowLayoutDirection) => ({
  'elk.algorithm': 'layered',
  'elk.direction': direction,
  'elk.layered.spacing.nodeNodeBetweenLayers': '150',
  'elk.spacing.nodeNode': '100',
  'elk.layered.spacing.edgeNodeBetweenLayers': '40',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.edgeRouting': 'ORTHOGONAL',
});

const UNKNOWN_STEP_TITLE = 'Unknown step';

export const formatNodeTitle = (type: string, catalog: WorkflowStepCatalog): string =>
  catalog.byType.get(type)?.label ?? UNKNOWN_STEP_TITLE;

/** What a step is called on the canvas: its name, or its type's label when it has none. */
export const stepTitle = (
  node: Pick<WorkflowDefinitionNode, 'type' | 'name'>,
  catalog: WorkflowStepCatalog,
): string => node.name || formatNodeTitle(node.type, catalog);

interface NodeVisualConfig {
  icon: string;
  iconColor?: string;
  headerColor: string;
  accentColor: string;
  detailsLabel?: string;
}

interface NodeVisualThemeConfig {
  headerColor: string;
  accentColor: string;
}

interface NodeVisualConfigEntry {
  icon: string;
  iconColor?: string;
  detailsLabel?: string;
  light: NodeVisualThemeConfig;
  dark: NodeVisualThemeConfig;
}

const DEFAULT_VISUAL_CONFIG: NodeVisualConfigEntry = {
  icon: 'category',
  detailsLabel: 'Expand details',
  light: {
    headerColor: '#EDF1F6',
    accentColor: '#F7F9FC',
  },
  dark: {
    headerColor: '#3B4A5A',
    accentColor: '#2B3847',
  },
};

/** Each step type the portal knows has its own look; the record makes a new type add one. */
const NODE_VISUAL_CONFIG: Record<WorkflowNodeTypeValue, NodeVisualConfigEntry> = {
  [WorkflowNodeType.Trigger]: {
    icon: 'play_circle',
    light: {
      headerColor: '#E1F5FE',
      accentColor: '#F0FBFF',
    },
    dark: {
      headerColor: '#1B4F5A',
      accentColor: '#0F3C47',
    },
  },
  [WorkflowNodeType.If]: {
    icon: 'schema',
    light: {
      headerColor: '#F0E8FF',
      accentColor: '#F9F5FF',
    },
    dark: {
      headerColor: '#3D2E5A',
      accentColor: '#2B1E42',
    },
  },
  // a branch like the if, chosen by the caller's key press
  [WorkflowNodeType.GatherMenu]: {
    icon: 'dialpad',
    light: {
      headerColor: '#F0E8FF',
      accentColor: '#F9F5FF',
    },
    dark: {
      headerColor: '#3D2E5A',
      accentColor: '#2B1E42',
    },
  },
  [WorkflowNodeType.Say]: {
    icon: 'campaign',
    light: {
      headerColor: '#E6ECFF',
      accentColor: '#EFF2FF',
    },
    dark: {
      headerColor: '#2C3A5A',
      accentColor: '#1F2A42',
    },
  },
  [WorkflowNodeType.CreateConversation]: {
    icon: 'call',
    light: {
      headerColor: '#E8F5E9',
      accentColor: '#F4FBF5',
    },
    dark: {
      headerColor: '#1E4620',
      accentColor: '#153016',
    },
  },
  [WorkflowNodeType.Play]: {
    icon: 'play_arrow',
    light: {
      headerColor: '#FFF3E0',
      accentColor: '#FFF8E7',
    },
    dark: {
      headerColor: '#4A3F1F',
      accentColor: '#362B14',
    },
  },
  [WorkflowNodeType.SetVariables]: {
    icon: 'dataset',
    light: {
      headerColor: '#EDE7F6',
      accentColor: '#F5F2FB',
    },
    dark: {
      headerColor: '#3B3350',
      accentColor: '#2A2438',
    },
  },
  [WorkflowNodeType.EnqueueForAgent]: {
    icon: 'support_agent',
    light: {
      headerColor: '#E1F5FE',
      accentColor: '#F0FBFF',
    },
    dark: {
      headerColor: '#1B4F5A',
      accentColor: '#0F3C47',
    },
  },
  [WorkflowNodeType.ConnectCallTo]: {
    icon: 'phone_forwarded',
    light: {
      headerColor: '#FFF8E1',
      accentColor: '#FFFCEB',
    },
    dark: {
      headerColor: '#4A4520',
      accentColor: '#362F14',
    },
  },
  [WorkflowNodeType.Voicemail]: {
    icon: 'voicemail',
    light: { headerColor: '#FCE4EC', accentColor: '#FDF2F6' },
    dark: { headerColor: '#4A2533', accentColor: '#361A25' },
  },
  [WorkflowNodeType.Callback]: {
    icon: 'phone_callback',
    light: { headerColor: '#FCE4EC', accentColor: '#FDF2F6' },
    dark: { headerColor: '#4A2533', accentColor: '#361A25' },
  },
  [WorkflowNodeType.CheckTimeAgainstSchedule]: {
    icon: 'schedule',
    light: { headerColor: '#E0F2F1', accentColor: '#F2FAF9' },
    dark: { headerColor: '#1D4440', accentColor: '#14312E' },
  },
  [WorkflowNodeType.CloseSmsConversation]: {
    icon: 'speaker_notes_off',
    light: { headerColor: '#ECEFF1', accentColor: '#F6F7F8' },
    dark: { headerColor: '#37474F', accentColor: '#263238' },
  },
};

/** The look of a step type; one the portal doesn't list gets the default look. */
const visualConfigFor = (type: string): NodeVisualConfigEntry =>
  isWorkflowNodeType(type) ? NODE_VISUAL_CONFIG[type] : DEFAULT_VISUAL_CONFIG;

const UNKNOWN_VISUAL_CONFIG: NodeVisualConfigEntry = {
  icon: 'help_outline',
  light: { headerColor: '#EEEEEE', accentColor: '#F7F7F7' },
  dark: { headerColor: '#3A3A3A', accentColor: '#2A2A2A' },
};

const getNodeVisualConfig = (
  type: string,
  isUnknown: boolean,
  isDarkMode = false,
): NodeVisualConfig => {
  const visualConfig = isUnknown ? UNKNOWN_VISUAL_CONFIG : visualConfigFor(type);
  const { light, dark, ...sharedConfig } = visualConfig;
  const themeConfig = isDarkMode ? dark : light;

  return {
    ...sharedConfig,
    ...themeConfig,
  };
};

/** The icon a step type is drawn with, for the + menu. */
export const getStepIcon = (type: string): string => visualConfigFor(type).icon;

/** Where connections meet a card: top and bottom down the page, left and right across it. */
const handlePositions = (direction: WorkflowLayoutDirection) =>
  direction === WorkflowLayoutDirection.Right
    ? { targetPosition: Position.Left, sourcePosition: Position.Right }
    : { targetPosition: Position.Top, sourcePosition: Position.Bottom };

/** The ELK port a branching step's output leaves from, pinned where the card draws its handle. */
const elkPort = (
  nodeId: string,
  { id: handle, offset }: SourceHandleOffset,
  { direction, width }: { direction: WorkflowLayoutDirection; width: number },
) => {
  const across = direction === WorkflowLayoutDirection.Right;
  return {
    id: `${nodeId}:${handle}`,
    width: 1,
    height: 1,
    x: across ? width : width * offset,
    y: across ? ELK_NODE_HEIGHT * offset : ELK_NODE_HEIGHT,
    layoutOptions: { 'elk.port.side': across ? 'EAST' : 'SOUTH' },
  };
};

// It applies automatic layout to the given nodes and edges using ELK
const applyElkLayout = async (
  nodes: WorkflowGraphNode[],
  edges: Edge[],
  {
    direction,
    stepWidths,
  }: {
    direction: WorkflowLayoutDirection;
    /** Each step's card width; add-step targets aren't in it and keep the fixed width. */
    stepWidths: ReadonlyMap<string, number>;
  },
): Promise<WorkflowGraphNode[]> => {
  if (!nodes.length) {
    return nodes;
  }

  // a port for each branching output something leaves from, at the offset the card draws it
  const offsetsByNode = new Map(
    nodes.flatMap((node) =>
      'sourceHandles' in node.data && node.data.sourceHandles
        ? [[node.id, node.data.sourceHandles] as const]
        : [],
    ),
  );
  const portsByNode = new Map<string, SourceHandleOffset[]>();
  const portedEdgeIds = new Set<string>();
  edges.forEach(({ id, source, sourceHandle }) => {
    const port = offsetsByNode.get(source)?.find((handle) => handle.id === sourceHandle);
    if (!port) return;
    portedEdgeIds.add(id);
    const ports = portsByNode.get(source) ?? [];
    if (!ports.includes(port)) portsByNode.set(source, [...ports, port]);
  });

  const graph = {
    id: 'workflow-root',
    layoutOptions: getElkOptions(direction),
    children: nodes.map((node) => {
      const width = stepWidths.get(node.id) ?? ELK_NODE_WIDTH;
      const handles = portsByNode.get(node.id);
      return {
        id: node.id,
        width,
        height: node.type === ADD_STEP_NODE_TYPE ? ELK_ADD_STEP_HEIGHT : ELK_NODE_HEIGHT,
        ...(handles && {
          layoutOptions: { 'elk.portConstraints': 'FIXED_POS' },
          ports: handles.map((handle) => elkPort(node.id, handle, { direction, width })),
        }),
      };
    }),
    edges: edges.map((edge) => ({
      id: edge.id,
      sources: [
        portedEdgeIds.has(edge.id) ? `${edge.source}:${edge.sourceHandle}` : edge.source,
      ],
      targets: [edge.target],
    })),
  };

  const layoutedGraph = await elk.layout(graph);
  const nodePositions = new Map(
    layoutedGraph.children?.map((child) => [child.id, child]) ?? [],
  );

  return nodes.map((node) => {
    const layoutNode = nodePositions.get(node.id);
    if (!layoutNode) {
      return node;
    }

    return {
      ...node,
      position: {
        x: layoutNode.x ?? node.position.x ?? 0,
        y: layoutNode.y ?? node.position.y ?? 0,
      },
      ...handlePositions(direction),
    };
  });
};

/** A true output is drawn green; every other connection in the outline colour. */
const edgeStyle = (condition: boolean | undefined, isDarkMode: boolean) => {
  if (condition === true) {
    return { stroke: isDarkMode ? '#66bb6a' : '#2e7d32', strokeWidth: 2 };
  }
  return { stroke: isDarkMode ? '#899294' : '#6f797a', strokeWidth: 1.5 };
};

const HIDDEN_MARKER = {
  type: MarkerType.ArrowClosed,
  width: 0,
  height: 0,
  color: 'transparent',
} as const;

export const buildReactFlowGraph = async (
  definition: WorkflowDefinition,
  {
    catalog,
    isDarkMode = false,
    layoutDirection = WorkflowLayoutDirection.Down,
    editable = false,
    folded = new Set(),
  }: BuildWorkflowGraphOptions,
): Promise<ReactFlowGraph> => {
  const shown = withoutFoldedSteps(definition, folded);
  const shownIds = new Set(shown.nodes.map(({ id }) => id));

  const stepNodes: Node<WorkflowNodeDisplayData>[] = shown.nodes.map((node, index) => {
    const isUnknown = !catalog.byType.has(node.type);
    const visualConfig = getNodeVisualConfig(node.type, isUnknown, isDarkMode);
    const position =
      node.position ??
      ({
        x: (index % COLUMN_COUNT) * COLUMN_WIDTH,
        y: Math.floor(index / COLUMN_COUNT) * ROW_HEIGHT,
      } as const);

    return {
      id: node.id,
      type: STEP_NODE_TYPE,
      data: {
        nodeType: node.type,
        nodeId: node.id,
        isUnknown,
        layoutWidth: getStepWidth(node, catalog),
        icon: visualConfig.icon,
        iconColor: visualConfig.iconColor,
        headerColor: visualConfig.headerColor,
        accentColor: visualConfig.accentColor,
        detailsLabel: visualConfig.detailsLabel,
        sourceHandles: sourceHandleOffsets(node, catalog),
        horizontal: layoutDirection === WorkflowLayoutDirection.Right,
      },
      position,
      ...handlePositions(layoutDirection),
      draggable: true,
      zIndex: index + 1,
    };
  });

  const stepEdges: WorkflowGraphEdge[] = shown.edges.map((edge, index) => {
    const condition = edge.data?.condition;
    const definitionEdgeId = edge.id ?? `edge-${index}`;
    let outputSuffix = '';
    if (typeof condition === 'boolean') {
      outputSuffix = condition ? '-true' : '-false';
    } else if (edge.data?.option) {
      outputSuffix = `-option-${edge.data.option}`;
    }

    return {
      id: `${definitionEdgeId}${outputSuffix}`,
      source: edge.source,
      target: edge.target,
      type: STEP_EDGE_TYPE,
      sourceHandle: outputHandleId(edge.data),
      animated: false,
      markerEnd: HIDDEN_MARKER,
      style: edgeStyle(condition, isDarkMode),
      data: { definitionEdgeId, editable },
    };
  });

  // In a draft, every output with nothing after it gets a target to add a step there — except the
  // outputs of an `if` or a Phone menu, which the card's own "+ <output>" chips add to.
  const openOutputs = editable
    ? getOpenOutputs(definition, catalog).filter(
        (insertion) => shownIds.has(insertion.sourceId) && !outputHandleId(insertion),
      )
    : [];
  const addStepNodes: Node<WorkflowAddStepNodeData>[] = openOutputs.map((insertion) => ({
    id: `add:${insertion.sourceId}:next`,
    type: ADD_STEP_NODE_TYPE,
    data: { insertion, horizontal: layoutDirection === WorkflowLayoutDirection.Right },
    position: { x: 0, y: 0 },
    draggable: false,
    selectable: false,
  }));
  const addStepEdges: WorkflowGraphEdge[] = addStepNodes.map((node) => ({
    id: `to-${node.id}`,
    source: node.data.insertion.sourceId,
    target: node.id,
    type: 'smoothstep',
    sourceHandle: outputHandleId(node.data.insertion),
    markerEnd: HIDDEN_MARKER,
    style: { ...edgeStyle(node.data.insertion.condition, isDarkMode), opacity: 0.5 },
  }));

  const nodes: WorkflowGraphNode[] = [...stepNodes, ...addStepNodes];
  const edges = [...stepEdges, ...addStepEdges];

  const stepWidths = new Map(stepNodes.map(({ id, data }) => [id, data.layoutWidth] as const));
  const layoutedNodes = await applyElkLayout(nodes, edges, {
    direction: layoutDirection,
    stepWidths,
  }).catch((error) => {
    console.warn(
      'Failed to layout workflow graph with ELK. Falling back to default layout.',
      error,
    );
    return nodes;
  });

  // Across the page, a + sits level with the step it follows: same top, and the card draws itself
  // as tall as that step (see AddStepNode), so the connection runs straight across.
  const horizontal = layoutDirection === WorkflowLayoutDirection.Right;
  const topById = new Map(layoutedNodes.map(({ id, position }) => [id, position.y]));
  const alignedNodes = horizontal
    ? layoutedNodes.map((node) => {
        if (node.type !== ADD_STEP_NODE_TYPE) return node;
        const sourceTop = topById.get(
          (node.data as WorkflowAddStepNodeData).insertion.sourceId,
        );
        return sourceTop === undefined
          ? node
          : { ...node, position: { ...node.position, y: sourceTop } };
      })
    : layoutedNodes;

  return { nodes: alignedNodes, edges };
};

/** A Phone menu's option keys in order, without the labels and digits typed into them. */
const menuOptionKeys = (data: WorkflowDefinitionNode['data']) => {
  const options = data?.options;
  return (Array.isArray(options) ? options : []).flatMap((option: unknown) => {
    const key = (option as { key?: unknown } | null)?.key;
    return typeof key === 'string' && key ? [{ key }] : [];
  });
};

/**
 * What the layout depends on — step ids, types, stored positions and widths, and the connections — as a
 * JSON string. Field values are left out, so editing a message doesn't move the canvas, and the
 * string parses back into a definition the graph can be built from. A Phone menu keeps its option
 * keys, in order: each is a port its connections leave from.
 */
export const getWorkflowGraphStructureKey = (definition: WorkflowDefinition): string =>
  JSON.stringify({
    nodes: definition.nodes.map(({ id, type, position, width, data }) => ({
      id,
      type,
      position,
      width,
      ...(type === WorkflowNodeType.GatherMenu && { data: { options: menuOptionKeys(data) } }),
    })),
    edges: definition.edges.map(({ id, source, target, data }) => ({
      id,
      source,
      target,
      data,
    })),
  } satisfies WorkflowDefinition);

export const parseWorkflowDefinition = (
  rawDefinition?: string | null,
): ParsedWorkflowDefinition => {
  if (!rawDefinition) {
    return { definition: null, error: null };
  }

  try {
    const parsed = JSON.parse(rawDefinition) as unknown;
    if (!isWorkflowDefinitionShape(parsed)) {
      throw new Error('Definition must include nodes and edges arrays.');
    }

    const nodes: WorkflowDefinitionNode[] = parsed.nodes
      .filter(isValidNodeShape)
      .map((node: ParsedNodeShape) => {
        // An unknown type is kept as stored: it renders as unknown and is saved back untouched.
        return {
          id: node.id,
          type: node.type,
          ...(typeof node.name === 'string' && node.name ? { name: node.name } : {}),
          data: node.data ?? {},
          position:
            typeof node.position?.x === 'number' && typeof node.position?.y === 'number'
              ? node.position
              : undefined,
          // a width that isn't a number is dropped, so the card falls back to its type's default
          ...(typeof node.width === 'number' && Number.isFinite(node.width)
            ? { width: node.width }
            : {}),
        };
      });

    const edges: WorkflowDefinitionEdge[] = parsed.edges
      .filter(isValidEdgeShape)
      .map((edge: ParsedEdgeShape, index: number) => ({
        id: edge.id ?? `edge-${index}`,
        source: edge.source,
        target: edge.target,
        data: edge.data,
      }));

    if (!nodes.length) {
      throw new Error('Definition does not contain any nodes.');
    }

    return { definition: { nodes, edges }, error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unable to parse definition.';
    return { definition: null, error: message };
  }
};
